import { useState, useEffect, useCallback } from 'react';
import Empty from 'constants/empty';
import { RESPONSE_TYPES, MESSAGE_TYPES } from 'ai/constants/superchat';
import { setLastFollowUps, removeLastFollowUps, getValidatedFollowUps } from 'ai/utils/sessionStorage';
import areFollowUpsSameAsPrompts from 'ai/utils/areFollowUpsSameAsPrompts';

const useSuperChatFollowUps = ({
    messages, sessionId, prompts, contextPrompts, entryPointData
}) => {
    const [followUps, setFollowUps] = useState([]);
    const [hasRestoredFollowUps, setHasRestoredFollowUps] = useState(false);

    const restoreStoredFollowUps = useCallback(
        (sessionIdentifier, currentContext) => {
            const validFollowUps = getValidatedFollowUps(sessionIdentifier, currentContext, prompts, areFollowUpsSameAsPrompts);

            if (validFollowUps) {
                setFollowUps(validFollowUps);
                setHasRestoredFollowUps(true);
            } else {
                setHasRestoredFollowUps(false);
            }
        },
        [prompts]
    );

    useEffect(() => {
        if (messages.length === 0) {
            return;
        }

        const lastMessage = messages.at(-1);

        if (lastMessage.response_type === RESPONSE_TYPES.FOLLOW_UP_MESSAGES_RESPONSE) {
            const newFollowUps = lastMessage?.data?.follow_ups || Empty.Array;

            const matchesPrompts = areFollowUpsSameAsPrompts(newFollowUps, prompts);
            const matchesContextPrompts = contextPrompts?.length > 0 && areFollowUpsSameAsPrompts(newFollowUps, contextPrompts);

            if (matchesPrompts || matchesContextPrompts) {
                if (hasRestoredFollowUps) {
                    return;
                }

                setFollowUps(Empty.Array);
                removeLastFollowUps();

                return;
            }

            const normalizedFollowUps = newFollowUps || Empty.Array;
            setFollowUps(normalizedFollowUps);
            setHasRestoredFollowUps(false);

            if (sessionId && normalizedFollowUps.length > 0) {
                const context = {
                    entrypoint: entryPointData?.entrypoint,
                    'product_id': entryPointData?.products?.[0]?.product_id,
                    'category_id': entryPointData?.category_id,
                    'search_term': entryPointData?.search_term
                };
                setLastFollowUps(sessionId, normalizedFollowUps, context);
            } else if (sessionId) {
                removeLastFollowUps();
            }
        } else {
            if (hasRestoredFollowUps && lastMessage.message_type !== MESSAGE_TYPES.USER_QUERY) {
                return;
            }

            setHasRestoredFollowUps(false);
            setFollowUps(Empty.Array);
            removeLastFollowUps();
        }
    }, [messages, sessionId, prompts, contextPrompts, hasRestoredFollowUps]);

    const clearFollowUps = useCallback(() => {
        setFollowUps([]);
        setHasRestoredFollowUps(false);
        removeLastFollowUps();
    }, []);

    return {
        followUps,
        hasRestoredFollowUps,
        restoreStoredFollowUps,
        clearFollowUps
    };
};

export default useSuperChatFollowUps;
